import { FC, useState, useEffect, useCallback, useRef } from 'react'
import {
  DefaultFormContainer,
  FormItemTitle,
  FlexDiv,
  MapFormContainer,
} from 'src/components/application/formComponents/styles'
import { AttorneyData } from 'src/dtos/applicationData'
import StepBullet from 'src/components/basic/StepBullet'
import Attorney from './Attorney'
import About from './About'
import Licensing from './Licensing'
import {
  NewFormAttorneyMainSchema,
  NewFormAttorneyAboutSchema,
  NewFormAttorneyLicenseSchema,
} from 'src/yup/NewAppSchema'

interface Props {
  attorney: AttorneyData;
  setAttorney: (changes: Partial<AttorneyData>) => void;
  attorneyNumber: number;
  removeAttorney: () => void;
  handleSaveDetails: () => Promise<void>;
  showErrors?: boolean;
  allAccordionsOpen?: boolean;
  disableAddNewAttorneys?: boolean;
  errorCodes: string[];
}

const NewItemForm: FC<Props> = ({
  attorney,
  setAttorney,
  removeAttorney,
  handleSaveDetails,
  attorneyNumber,
  showErrors,
  allAccordionsOpen,
  disableAddNewAttorneys,
  errorCodes = [],
}) => {
  const [sectionStatus, setSectionStatus] = useState({
    attorney: { isAccordion: false, isOpen: true },
    about: { isAccordion: false, isOpen: false },
    license: { isAccordion: false, isOpen: false },
    history: { isAccordion: false, isOpen: false }
  });
  const hasValidated = useRef(false);

  const validateMainSection = useCallback(() => {
    try {
      NewFormAttorneyMainSchema.validateSync(attorney)
      return true
    } catch (e) {
      return false
    }
  }, [attorney])

  const validateAboutSection = useCallback(() => {
    try {
      NewFormAttorneyAboutSchema.validateSync(attorney)
      return true
    } catch (e) {
      return false
    }
  }, [attorney])
  
  const validateLicenseSection = useCallback(() => {
    try {
      NewFormAttorneyLicenseSchema.validateSync(attorney?.license)
      return true
    } catch (e) {
      return false
    }
  }, [attorney?.license])

  const initialValidation = useCallback(async () => {
    let status = { ...sectionStatus }

    if (allAccordionsOpen === true) {
      Object.keys(status).forEach(key => {
        status[key as keyof typeof status] = { isAccordion: true, isOpen: true }
      })
    } else {
      if (validateMainSection() === false) {
        return
      } else {
        status.attorney.isAccordion = true
        status.attorney.isOpen = false
      }

      if (validateAboutSection() === false) {
        status.about.isOpen = true
        setSectionStatus(status)
        return
      } else {
        status.about.isAccordion = true
      }

      if (validateLicenseSection() === false) {
        status.license.isOpen = true
        setSectionStatus(status)
        return
      } else {
        status.license.isAccordion = true
      }
    }


    setSectionStatus(status)
  }, [allAccordionsOpen, sectionStatus, validateAboutSection, validateLicenseSection, validateMainSection])

  const onSaveDetails = async (section: string) => {
    await handleSaveDetails()
    if (allAccordionsOpen !== true) {
      setSectionStatus(prev => {
        let nextSection = {}
        switch (section) {
          case 'attorney':
            nextSection = { about: { isAccordion: false, isOpen: true } }
            break
          case 'about':
            nextSection = { license: { isAccordion: false, isOpen: true } }
            break
          case 'license':
            nextSection = { history: { isAccordion: false, isOpen: true } }
            break
        }

        return { ...prev, ...nextSection, [section]: { isAccordion: true, isOpen: false } }
      })
    }
  }

  if (!hasValidated.current) {
    initialValidation();
    hasValidated.current = true;
  }

  return (
    <MapFormContainer >
      <DefaultFormContainer>
        <FlexDiv>
          <StepBullet id={`att__${attorneyNumber + 1}`}>{attorneyNumber + 1}</StepBullet>
          <FlexDiv dir="column" maxWidth='460px'>
            <FormItemTitle>Attorney {attorneyNumber + 1}</FormItemTitle>
            <Attorney
              attorney={attorney}
              setAttorneyInfo={setAttorney}
              attorneyNumber={attorneyNumber + 1}
              removeAttorney={removeAttorney}
              status={sectionStatus.attorney}
              toggleOpenAccordion={() => setSectionStatus(prev => ({ ...prev, attorney: { ...prev.attorney, isOpen: !prev.attorney.isOpen } }))}
              isIncorrect={showErrors}
              disableAddNewAttorneys={disableAddNewAttorneys}
              errorCodes={errorCodes}
            />
            {(sectionStatus.about.isAccordion === false && sectionStatus.about.isOpen === false) ? null : (
              <About
                attorney={attorney}
                setAttorneyInfo={setAttorney}
                handleSaveDetails={onSaveDetails}
                status={sectionStatus.about}
                toggleOpenAccordion={() => setSectionStatus(prev => ({ ...prev, about: { ...prev.about, isOpen: !prev.about.isOpen } }))}
                isIncorrect={showErrors}
                errorCodes={errorCodes}
              />
            )}
            {(sectionStatus.license.isAccordion === false && sectionStatus.license.isOpen === false) ? null : (
              <Licensing
                attorney={attorney}
                setAttorneyInfo={setAttorney}
                handleSaveDetails={onSaveDetails}
                status={sectionStatus.license}
                toggleOpenAccordion={() => setSectionStatus(prev => ({ ...prev, license: { ...prev.license, isOpen: !prev.license.isOpen } }))}
                isIncorrect={showErrors}
                errorCodes={errorCodes}
              />
            )}
          </FlexDiv>
        </FlexDiv>
      </DefaultFormContainer>
    </MapFormContainer>
  )
}

export default NewItemForm